import { OrderDto } from '../../types/opsBriefing';

/**
 * 철회가 강사에게 닿았는가 — 화면이 읽는 말로.
 *
 * ═══════════════════════════════════════════════════════════
 * 이 파일이 지키는 한 가지
 * ═══════════════════════════════════════════════════════════
 * **모르는 것을 "전달됨"으로 말하지 않습니다.**
 *
 * 철회는 전달돼야 뜻이 있습니다. 알림이 실패했는데 아무도 모르면 강사는
 * 취소된 지시를 그대로 실행하고 회원에게 연락합니다 — 원장이 막으려던
 * 바로 그 일이 그대로 일어납니다. 그래서 네 상태를 뭉개지 않습니다.
 *
 * 같은 화면의 다른 자리도 같은 규칙을 씁니다. "지켜낸 매출"을 "완료된 건의
 * 대상 금액"으로 고친 것, 금액 산정 불가를 0원으로 세지 않는 것, 히트맵이
 * "브리핑이 없던 날"과 "챙길 일이 0건이던 날"을 가르는 것 — 전부 같은 이유입니다.
 *
 * 순수 함수로 떼어 둔 것은 문구를 테스트로 잠그기 위해서입니다. 화면 안에
 * 삼항 연산자로 묻어 두면 다음 사람이 네 갈래를 두 갈래로 줄이게 됩니다.
 */

/** 화면이 실제로 가르는 네 갈래 */
export type RetractDeliveryKind =
    /** 강사가 보기 전에 거둬들였다 (retractDelivery === null) */
    | 'RECALLED'
    /**
     * 서버가 이 값을 안 보냈다 — **회수가 아니다.**
     *
     * `null`(회수)과 합치면 안 됩니다. `null` 은 서버가 "보낼 대상이 아니다"라고
     * **말해 준 것**이고, 키가 없는 것은 우리가 **못 들은 것**입니다. 둘을 같게
     * 다루면 모르는 것을 아는 척하게 됩니다 — 이 파일이 막으려는 바로 그것입니다.
     */
    | 'UNKNOWN'
    /** 보내는 중 — **보냈다가 아니다** */
    | 'PENDING'
    /** 알림함에 남았다 */
    | 'DELIVERED'
    /** 못 보냈다. 원장이 직접 연락해야 한다 */
    | 'FAILED';

export type RetractDeliveryView = {
    kind: RetractDeliveryKind;
    /** 한 줄로 끝나는 사실. 이것만 읽어도 뜻이 통해야 한다 */
    title: string;
    /** 왜 그런지, 그래서 뭘 해야 하는지 */
    detail: string;
    /**
     * 원장이 **지금 뭔가 해야 하는가**.
     *
     * FAILED 만 true 입니다. PENDING 은 서버가 계속 시도 중이라 원장이 할 일이
     * 없고, RECALLED 는 애초에 정상 동작입니다. 여기에 PENDING 을 얹으면
     * 매일 아침 화면에 붉은 줄이 늘어나고, 진짜 붉은 줄이 묻힙니다.
     */
    needsAction: boolean;
    tone: { color: string; bg: string; border: string };
};

const TONE = {
    /** 아무 일도 안 일어난 것이 정상 — 회색. 실패로 그리면 안 된다 */
    quiet: { color: '#5B6B7F', bg: '#F7F9FC', border: '#DCE4EE' },
    /** 진행 중 — 호박색. 붉은색을 쓰면 원장이 잘못된 줄 알고 강사에게 전화한다 */
    waiting: { color: '#B45309', bg: '#FFFAF0', border: '#F5D08A' },
    /** 닿았다 — 초록 */
    landed: { color: '#00795F', bg: '#F0F7F4', border: '#C6E5DA' },
    /** 못 닿았다 — 붉은색. 원장이 움직여야 하는 유일한 상태 */
    broken: { color: '#C22D38', bg: '#FFF6F6', border: '#FBD0D3' },
};

/**
 * 철회된 지시의 전달 상태.
 *
 * 철회되지 않은 지시에는 **null** 을 돌려줍니다 — 화면이 아무것도 그리지 않게.
 * `retractDelivery` 가 null 인 것과 이 함수가 null 을 돌려주는 것은 다른 뜻입니다.
 * 앞은 "회수라 안 보냈다", 뒤는 "철회 자체가 없다" 입니다.
 */
export const describeRetractDelivery = (
    order: Pick<OrderDto, 'status' | 'retractDelivery' | 'coachName'> & {
        retractDeliveredLabel?: string;
    }
): RetractDeliveryView | null => {
    if (order.status !== 'RETRACTED') return null;

    const coach = order.coachName ? `${order.coachName} 강사` : '담당 강사';

    /*
     * 키가 아예 없는 응답(구버전 서버·부분 응답)은 **모른다**로 답합니다.
     * `null` 로 눕혀서 회수라고 말하면, 통보 갈래였는지 회수 갈래였는지
     * 알지도 못하면서 "알림은 가지 않았습니다"라고 단정하게 됩니다.
     */
    if (order.retractDelivery === undefined) {
        return {
            kind: 'UNKNOWN',
            title: '강사에게 전달됐는지 확인하지 못했습니다',
            detail: `이 지시가 ${coach}에게 어떻게 처리됐는지 화면이 알지 못합니다. 새로고침해도 그대로면 직접 확인해 주세요.`,
            needsAction: false,
            tone: TONE.waiting,
        };
    }

    switch (order.retractDelivery) {
        /*
         * 회수 — 강사가 보기 전에 거둬들였다.
         *
         * ⚠️ 이것을 "전달 실패"로 그리면 안 됩니다. 아무것도 안 보낸 것이 **정상**입니다.
         *    본 적 없는 사람에게 "취소됐습니다"를 알리는 것은 없던 일을 만드는 것이라,
         *    서버가 일부러 알림을 보내지 않습니다.
         */
        case null:
            return {
                kind: 'RECALLED',
                title: '강사가 열어 보기 전에 회수했습니다',
                detail: `${coach}는 이 지시를 본 적이 없습니다. 강사 앱에서 조용히 내려갔고, 알림도 가지 않았습니다.`,
                needsAction: false,
                tone: TONE.quiet,
            };

        /*
         * 보내는 중 — **"보냈다"가 아닙니다.**
         *
         * 여기서 "전달했습니다"라고 쓰면 원장은 손을 뗍니다. 그런데 강사는 아직
         * 모르고, 취소된 지시를 그대로 실행해 회원에게 연락합니다.
         */
        case 'PENDING':
            return {
                kind: 'PENDING',
                title: '아직 강사에게 닿지 않았습니다',
                detail: `철회 알림을 다시 보내는 중입니다. ${coach}에게 아직 닿지 않았으니, 급한 건이면 직접 말씀해 주세요.`,
                needsAction: false,
                tone: TONE.waiting,
            };

        /*
         * 닿았다 — 기준은 **알림함 기록**입니다.
         *
         * "강사가 읽었다"가 아닙니다. 그건 우리가 모릅니다. 푸시가 실패했어도,
         * 강사가 알림을 꺼 뒀어도 알림함에는 남아 있어 앱을 열면 봅니다.
         * 읽었다고 단정하면 또 모르는 것을 말하는 셈이 됩니다.
         */
        case 'DELIVERED':
            return {
                kind: 'DELIVERED',
                title: order.retractDeliveredLabel
                    ? `강사 알림함에 전달됐습니다 · ${order.retractDeliveredLabel}`
                    : '강사 알림함에 전달됐습니다',
                detail: `${coach}가 앱을 열면 철회 사실을 봅니다.`,
                needsAction: false,
                tone: TONE.landed,
            };

        /*
         * 못 닿았다 — 원장이 움직여야 하는 유일한 상태.
         *
         * 서버는 상한 5회 / 시효 6시간까지 시도하고 포기합니다. 여기까지 왔으면
         * 다시 시도해도 안 갑니다. 조용한 회색으로 두면 강사가 취소된 지시를
         * 그대로 실행할 수 있습니다. 그렇다고 겁을 주지도 않습니다 —
         * **할 일 하나**만 적습니다.
         *
         * ⚠️ "강사가 모른다"고 단정하지 않습니다. FAILED 는 **알림함 기록이
         *    실패했다**는 뜻이지 강사의 인지 여부가 아닙니다. 강사가 앱에서
         *    철회 카드를 직접 봤을 수도, 원장이 이미 전화했을 수도 있습니다.
         *    확실한 것은 '닿지 않았다'까지이고, 원장이 할 일은 그것만으로 정해집니다.
         */
        case 'FAILED':
            return {
                kind: 'FAILED',
                title: '강사에게 전달하지 못했습니다',
                detail: `철회 알림이 ${coach}에게 닿지 않았습니다. 직접 연락해 주세요.`,
                needsAction: true,
                tone: TONE.broken,
            };

        default:
            return null;
    }
};

/**
 * 이 지시를 철회하면 **강사에게 알림이 가는가** — 화면이 사유를 요구할지 정하는 값.
 *
 * ⚠️ 이건 **추정**입니다. 최종 판정은 서버가 합니다.
 *
 * 서버는 저장된 두 사실(`seenAt` · `coachVisibleAt`)로 갈래를 정하는데, 화면은
 * `coachVisibleAt` 을 받지 못하고 `seenAt` 도 마지막 조회 시점의 값입니다.
 * 그래서 두 방향으로 어긋날 수 있습니다.
 *
 *   · 화면은 회수라 봤는데 서버는 통보 → 400 이 옵니다. 그때 사유를 받아 다시 묻습니다
 *     (OrderRetractModal 의 reasonRequiredByServer).
 *   · 화면은 통보라 봤는데 서버는 회수 → 사유를 한 번 더 물은 것뿐이라 손해가 없습니다.
 *
 * 화면이 굳이 먼저 판정하는 이유는, 가장 흔한 경우 — 보내자마자 잘못 보낸 걸
 * 알아채는 경우 — 에 읽을 사람도 없는 글을 쓰느라 원장을 멈춰 세우지 않기 위해서입니다.
 */
export const notifiesCoachOnRetract = (order: Pick<OrderDto, 'seenAt'>): boolean => !!order.seenAt;
